import { computed, type Ref } from 'vue';
import type { CatalogueItemGroup } from '@/models/Catalogue';
import {
  getAllCatalogueGroupsFlat,
  getCatalogueGroupPath,
} from './useCatalogueGroupHierarchy';

export interface CatalogueGroupAutocompleteOption {
  title: string;
  value: string;
  path: string;
}

/**
 * Composable for creating catalogue group autocomplete options with full hierarchy paths
 */
export const useCatalogueGroupAutocomplete = (
  groups: Ref<CatalogueItemGroup[]>,
) => {
  /**
   * Get all catalogue groups as autocomplete options with full hierarchy paths
   */
  const autocompleteOptions = computed(
    (): CatalogueGroupAutocompleteOption[] => {
      const allGroups = getAllCatalogueGroupsFlat(groups.value);
      return allGroups.map((group) => ({
        title: group.name,
        value: group.uuid,
        path: getCatalogueGroupPath(group, allGroups),
      }));
    },
  );

  /**
   * Filter catalogue groups by search term and rebuild hierarchy with matching groups and their parents
   */
  const getFilteredGroups = (searchTerm: string): CatalogueItemGroup[] => {
    if (!searchTerm) {
      return groups.value;
    }

    const searchLower = searchTerm.toLowerCase();
    const allGroups = getAllCatalogueGroupsFlat(groups.value);

    const matches = (g: CatalogueItemGroup): boolean => {
      if (g.name.toLowerCase().includes(searchLower)) {
        return true;
      }
      const path = getCatalogueGroupPath(g, allGroups).toLowerCase();
      return path.includes(searchLower);
    };

    // Helper: deep clone a group including ALL descendants
    const cloneWithAllDescendants = (
      g: CatalogueItemGroup,
    ): CatalogueItemGroup => {
      return {
        ...g,
        children: g.children
          ? g.children.map((c) => cloneWithAllDescendants(c))
          : [],
      };
    };

    // Rebuild the hierarchy: include full subtree when the node matches;
    // otherwise, include only branches that lead to matches (to keep ancestors).
    const buildHierarchy = (
      groupList: CatalogueItemGroup[],
    ): CatalogueItemGroup[] => {
      const result: CatalogueItemGroup[] = [];

      groupList.forEach((group) => {
        const selfMatches = matches(group);

        if (selfMatches) {
          // If the group itself matches, include the entire subtree (all children)
          result.push(cloneWithAllDescendants(group));
          return;
        }

        // Otherwise, check descendants
        if (group.children && group.children.length > 0) {
          const filteredChildren = buildHierarchy(group.children);
          if (filteredChildren.length > 0) {
            // Keep ancestor with only matching branches to show full path upwards
            result.push({
              ...group,
              children: filteredChildren,
            });
          }
        }
      });

      return result;
    };

    return buildHierarchy(groups.value);
  };

  return {
    autocompleteOptions,
    getFilteredGroups,
  };
};
